import { useEffect, useRef, useState } from "react"

import { CheckCircleOutline, ExpandMore, GroupsOutlined, Search, SportsSoccer } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material"
import { useInitData } from "@vkruglikov/react-telegram-web-app"

import { appRequest, canFilterChat, createMockData, isMockMode, MockRole } from "../../entities/chats/api"
import { InitDataResponse } from "../../entities/chats/model"
import PaidPlaces from "../PaidPlaces"
import UserRank from "../UserRank"

import "./listing.scss"

type NameEdit = { name: string; chatId?: number; uid?: string }
const Listing = () => {
  const [initDataUnsafe, initData] = useInitData()
  const [data, setData] = useState<InitDataResponse>({ common_name: "", chats: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [pending, setPending] = useState(0)
  const [role, setRole] = useState<MockRole>("user")
  const [reload, setReload] = useState(0)
  const [chatFilters, setChatFilters] = useState<Record<number, string>>({})
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const edits = useRef(new Map<string, NameEdit>())
  const queues = useRef(new Map<string, Promise<void>>())

  useEffect(() => {
    let active = true
    setLoading(true)
    setError("")
    const request = isMockMode ? Promise.resolve(createMockData(role)) : appRequest("init", initData)
    request
      .then((result) => {
        if (active && result) setData(result)
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [initData, role, reload])

  useEffect(() => {
    const currentTimers = timers.current
    return () => currentTimers.forEach(clearTimeout)
  }, [])

  const save = (path: string, params: object, key: string): Promise<void> => {
    setPending((count) => count + 1)
    const request = (queues.current.get(key) || Promise.resolve()).then(async () => {
      try {
        await appRequest(path, initData, params)
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "Не удалось сохранить изменения.")
        throw reason
      } finally {
        setPending((count) => count - 1)
      }
    })
    queues.current.set(
      key,
      request.catch(() => {}),
    )
    return request
  }
  const flushName = (key: string) => {
    clearTimeout(timers.current.get(key))
    timers.current.delete(key)
    const edit = edits.current.get(key)
    if (!edit || !edit.name.trim()) return
    edits.current.delete(key)
    save("set-name", edit, key).catch(() => {
      if (!edits.current.has(key)) edits.current.set(key, edit)
    })
  }
  const changeName = (name: string, chatId?: number, uid?: string) => {
    const key = chatId === undefined ? "common" : `${chatId}:${uid}`
    setData((previous) =>
      chatId === undefined
        ? { ...previous, common_name: name }
        : {
            ...previous,
            chats: previous.chats.map((chat) =>
              chat.id !== chatId
                ? chat
                : {
                    ...chat,
                    users: chat.users.map((user) => (user.uid !== uid ? user : { ...user, name })),
                  },
            ),
          },
    )
    edits.current.set(key, { name, chatId, uid })
    clearTimeout(timers.current.get(key))
    timers.current.set(
      key,
      setTimeout(() => flushName(key), 3000),
    )
  }
  const setPaid = (chatId: number, weekday: string, uids: string[]) =>
    setData((previous) => ({
      ...previous,
      chats: previous.chats.map((chat) =>
        chat.id !== chatId ? chat : { ...chat, paid: { ...chat.paid, [weekday]: uids } },
      ),
    }))
  const changePaid = (chatId: number, weekday: string, uids: string[], previousUids: string[]) => {
    setPaid(chatId, weekday, uids)
    save("set-paid", { chatId, weekday: Number(weekday), uids }, `paid:${chatId}:${weekday}`).catch(() =>
      setPaid(chatId, weekday, previousUids),
    )
  }
  const changeRole = (next: MockRole | null) => {
    if (!next) return
    timers.current.forEach(clearTimeout)
    timers.current.clear()
    edits.current.clear()
    setChatFilters({})
    setRole(next)
  }

  return (
    <main className="main-app">
      <header className="app-header">
        <div className="brand">
          <span className="brand-icon">
            <SportsSoccer />
          </span>
          <span>
            COACH<span className="brand-caption">ВАША КОМАНДА. ВАШЕ ИМЯ.</span>
          </span>
        </div>
        <span className="save-status" role="status">
          {pending ? (
            <>
              <CircularProgress size={15} /> Сохраняем
            </>
          ) : (
            <>
              <CheckCircleOutline fontSize="small" /> {isMockMode ? "Демо-режим" : "Автосохранение"}
            </>
          )}
        </span>
      </header>
      {isMockMode && (
        <div className="demo-panel">
          <div>
            <strong>Локальная отладка</strong>
            <p>Тестовые данные. Изменения сбрасываются при перезагрузке.</p>
          </div>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={role}
            onChange={(_, next) => changeRole(next)}
            aria-label="Роль в демо"
          >
            <ToggleButton value="user">Участник</ToggleButton>
            <ToggleButton value="admin">Админ</ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}
      <section className="intro">
        <span className="eyebrow">ЛИЧНЫЙ ПРОФИЛЬ</span>
        <p>Настройте имя, по которому вас узнает команда.</p>
      </section>
      {error && (
        <Alert severity="error" className="error-message" onClose={() => setError("")}>
          {error}{" "}
          <Button
            size="small"
            onClick={() => {
              setError("")
              if (edits.current.size) Array.from(edits.current.keys()).forEach(flushName)
              else setReload((value) => value + 1)
            }}
          >
            Повторить
          </Button>
        </Alert>
      )}
      {loading ? (
        <div className="loading-state" role="status">
          <CircularProgress size={30} />
          <p>Загружаем ваш профиль…</p>
        </div>
      ) : (
        <>
          <section className="profile-card">
            <div className="section-heading">
              <span className="section-number">01</span>
              <div>
                <h2>Основное имя</h2>
                <p>Так вас видят в чатах по умолчанию</p>
              </div>
            </div>
            <TextField
              label="Ваше имя"
              fullWidth
              value={data.common_name}
              onChange={(event) => changeName(event.target.value)}
              onBlur={() => flushName("common")}
              helperText="Сохраняется автоматически после ввода"
            />
          </section>
          <section className="chat-list">
            <div className="section-heading">
              <span className="section-number">02</span>
              <div>
                <h2>
                  Имя в чатах <span className="count">{data.chats.length}</span>
                </h2>
                <p>Для каждой команды можно выбрать своё</p>
              </div>
            </div>
            {data.chats.length === 0 && (
              <div className="empty-state">
                <GroupsOutlined />
                <h3>Здесь появятся ваши чаты</h3>
                <p>Пока нет доступных команд.</p>
              </div>
            )}
            {data.chats.map((chat, index) => {
              const canFilter = canFilterChat(chat, data, initDataUnsafe?.user?.id?.toString())
              const query = canFilter ? (chatFilters[chat.id] || "").trim().toLowerCase() : ""
              const users = chat.users.filter(
                (user) => !query || `${user.name} ${user.title}`.toLowerCase().includes(query),
              )
              return (
                <Accordion
                  key={`${role}:${chat.id}`}
                  defaultExpanded={
                    chat.id === Number(initDataUnsafe?.start_param) || (!initDataUnsafe?.start_param && index === 0)
                  }
                  disableGutters
                  elevation={0}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls={`chat-${chat.id}`}
                    id={`chat-heading-${chat.id}`}
                  >
                    <span className="chat-icon">
                      <GroupsOutlined />
                    </span>
                    <span className="chat-heading">
                      <strong>{chat.title}</strong>
                      <span>{canFilter ? `Участников: ${chat.users.length}` : "Ваш профиль в команде"}</span>
                    </span>
                    {canFilter && <Chip label="Админ" size="small" className="admin-chip" />}
                  </AccordionSummary>
                  <AccordionDetails id={`chat-${chat.id}`}>
                    {canFilter && (
                      <TextField
                        label="Поиск по имени или титулу"
                        size="small"
                        fullWidth
                        value={chatFilters[chat.id] || ""}
                        onChange={(event) =>
                          setChatFilters((previous) => ({ ...previous, [chat.id]: event.target.value }))
                        }
                        className="chat-filter"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    {canFilter && chat.paid && (
                      <PaidPlaces
                        paid={chat.paid}
                        users={chat.users}
                        onChange={(weekday, uids) => changePaid(chat.id, weekday, uids, chat.paid?.[weekday] || [])}
                      />
                    )}
                    {users.length === 0 && <p className="empty-search">Никого не нашли. Попробуйте другое имя.</p>}
                    {users.map((user, userIndex) => (
                      <div key={user.uid || userIndex} className="chat-user-item">
                        <TextField
                          label={user.title || "Имя в чате"}
                          fullWidth
                          value={user.name}
                          onChange={(event) => changeName(event.target.value, chat.id, user.uid)}
                          onBlur={() => flushName(`${chat.id}:${user.uid}`)}
                        />
                        {user.rank && (
                          <div className="user-rank">
                            <span className="rank-label">РАНГ ИГРОКА</span>
                            <UserRank
                              value={user.rank}
                              label={user.title}
                              onChange={(rank) => {
                                setData((previous) => ({
                                  ...previous,
                                  chats: previous.chats.map((item) =>
                                    item.id !== chat.id
                                      ? item
                                      : {
                                          ...item,
                                          users: item.users.map((member) =>
                                            member.uid !== user.uid ? member : { ...member, rank },
                                          ),
                                        },
                                  ),
                                }))
                                save(
                                  "set-rank",
                                  { rank, chatId: chat.id, uid: user.uid },
                                  `rank:${chat.id}:${user.uid}`,
                                ).catch(() => {
                                  setData((previous) => ({
                                    ...previous,
                                    chats: previous.chats.map((item) =>
                                      item.id !== chat.id
                                        ? item
                                        : {
                                            ...item,
                                            users: item.users.map((member) =>
                                              member.uid !== user.uid || member.rank !== rank
                                                ? member
                                                : { ...member, rank: user.rank },
                                            ),
                                          },
                                    ),
                                  }))
                                })
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )
            })}
          </section>
          <footer>Меньше настроек. Больше игры.</footer>
        </>
      )}
    </main>
  )
}
export default Listing
